import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { dateToString, localeNormalizer } from '@youshift/shared/utils';

import i18n from '../../../utils/i18n';

interface MonthNavigationProps {
  start: Date;
  end: Date;
  currentMonth: Date;
  onMonthChange: (newMonth: Date) => void;
}

export function MonthNavigation({
  start,
  end,
  currentMonth,
  onMonthChange,
}: MonthNavigationProps) {
  const locale = localeNormalizer(i18n.language);
  const [isAnimating, setIsAnimating] = useState(false);

  // const monthsInRange: Date[] = [];
  // let current = new Date(start.getUTCFullYear(), start.getUTCMonth(), 1);
  // console.log(current, end, start);
  // while (current <= end) {
  //   monthsInRange.push(current);
  //   current = new Date(current.getUTCFullYear(), current.getUTCMonth() + 1, 1);
  // }
  const startYear = start.getUTCFullYear();
  const startMonth = start.getUTCMonth();
  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth();

  // how many months between start and end, inclusive
  const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth);

  const monthsInRange: Date[] = [];
  for (let i = 0; i <= totalMonths; i++) {
    monthsInRange.push(new Date(startYear, startMonth + i, 1));
  }

  const currentIndex = monthsInRange.findIndex(
    month =>
      month.getUTCMonth() === currentMonth.getUTCMonth() &&
      month.getUTCFullYear() === currentMonth.getUTCFullYear(),
  );

  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (isAnimating) return;

    setIsAnimating(true);
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < monthsInRange.length) {
      onMonthChange(monthsInRange[newIndex]);
    }
    setTimeout(() => setIsAnimating(false), 300);
  };

  if (monthsInRange.length <= 1) {
    return (
      <div className="text-center text-lg font-semibold py-2">
        {dateToString(currentMonth, 'month-year-local', locale)}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-4 py-2">
      <button
        onClick={() => handleMonthChange('prev')}
        disabled={currentIndex === 0}
        className={`p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
          isAnimating ? 'opacity-50' : ''
        }`}
        aria-label="Previous month"
      >
        <ChevronLeftIcon className="w-6 h-6" />
      </button>
      <div className="text-lg font-semibold">
        {dateToString(currentMonth, 'month-year-local', locale)}
      </div>
      <button
        onClick={() => handleMonthChange('next')}
        disabled={currentIndex === monthsInRange.length - 1}
        className={`p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
          isAnimating ? 'opacity-50' : ''
        }`}
        aria-label="Next month"
      >
        <ChevronRightIcon className="w-6 h-6" />
      </button>
    </div>
  );
}
